// "Set the clock" questions (21 Oct 2026, Year 3): the question gives a time
// in words and the student moves the hands of a clock to show it. An answer
// is sent, and its answer key is stored, as the one string "time=<h>:<mm>"
// ("time=5:47", "time=12:00"), and the two are marked by comparing those
// strings.

export type ClockTime = { hour: number; minute: number };

// Where the hands start: 12 o'clock.
export const CLOCK_START: ClockTime = { hour: 12, minute: 0 };

function isWholeIn(value: unknown, low: number, high: number): value is number {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= low &&
    value <= high
  );
}

export function readClockTime(value: unknown): ClockTime | null {
  if (typeof value !== 'object' || value === null) return null;
  const { hour, minute } = value as Record<string, unknown>;
  return isWholeIn(hour, 1, 12) && isWholeIn(minute, 0, 59)
    ? { hour, minute }
    : null;
}

// The one entry, "time=5:47". Null for a time a 12-hour clock cannot show.
export function clockEntry(time: ClockTime): string | null {
  const read = readClockTime(time);
  return read
    ? `time=${String(read.hour)}:${String(read.minute).padStart(2, '0')}`
    : null;
}

// The other way: "time=5:47" back into its time. Null for anything else.
export function parseClockEntry(entry: unknown): ClockTime | null {
  if (typeof entry !== 'string') return null;
  const match = /^time=(\d{1,2}):(\d{2})$/.exec(entry);
  if (!match) return null;
  return readClockTime({ hour: Number(match[1]), minute: Number(match[2]) });
}

// The hands moved on (or back, for a negative number) by some minutes. The
// minute hand carries the hour hand with it, like a real clock: 12:58 on 5
// minutes is 1:03, and 12:00 back 1 minute is 11:59.
export function moveClock(time: ClockTime, minutes: number): ClockTime {
  const total = ((time.hour % 12) * 60 + time.minute + minutes) % 720;
  const inDay = total < 0 ? total + 720 : total;
  const hour = Math.floor(inDay / 60);
  return { hour: hour === 0 ? 12 : hour, minute: inDay % 60 };
}

// The practice screen keeps every answer as one piece of text: here the time
// as "5:47". Empty text means the hands have not moved from 12 o'clock.
export function splitClockText(value: string): ClockTime {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  const read = match
    ? readClockTime({ hour: Number(match[1]), minute: Number(match[2]) })
    : null;
  return read ?? CLOCK_START;
}

export function joinClockText(time: ClockTime): string {
  return `${String(time.hour)}:${String(time.minute).padStart(2, '0')}`;
}
